import '@fontsource/roboto/300.css';
import '@fontsource/roboto/400.css';
import '@fontsource/roboto/500.css';
import '@fontsource/roboto/700.css';
import {
  Box,
  CssBaseline,
  Paper,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import SideNavBar from './components/side-nav-bar/SideNavBar';
import CustomAppBar from './components/custom-app-bar/CustomAppBar';
import {
  Outlet,
  useLocation,
  useNavigate,
  useSearchParams,
} from 'react-router-dom';
import styleVariables from './scss/_variables.scss';
import styles from './scss/App.module.scss';
import { Footer } from './components/footer/Footer';
import { useEffect, useState } from 'react';
import DashboardTour from 'src/components/dashboard-tour/DashboardTour';
import DnsVerification from 'src/components/dns-verification/DnsVerification';
import { observer } from 'mobx-react-lite';
import { ROLES } from 'src/constants/Role';
import { useAlertStore } from 'src/stores/AlertStore/AlertStore';
import { useAuthStore } from 'src/stores/AuthStore/AuthStore';
import { useOrganizationStore } from 'src/stores/OrganizationStore/OrganizationStore';
import { UNHANDLED_ERROR } from './constants/errors';
import { ENTRY_CASES, PATHS, SEARCH_PARAMS } from './constants/navigateRoutes';
import UpgradeRequestPopup from './components/upgrade-request-popup/UpgradeRequestPopup';
import { getAchievementStatuses } from './services/achievementEngine';
import { AchievementStatus } from './types/achievementEngine';
import { useLanguageStore } from './stores/LanguageStore/LanguageStore';
import { useUserSettingsStore } from './stores/UserSettingsStore/UserSettingsStore';
import { notifyUnhandledError } from './utils/commonErrorUtils';
import AdultConsentDialog from './components/adult-consent/AdultConsentDialog';
import { updateAdultConsent } from './services/user';
import { useTranslation } from 'react-i18next';
import { trackDashboardPageView } from 'src/utils/analytics';
import BillingTrialBanner from './components/billing-trial-banner/BillingTrialBanner';
import RevampOnboardingBootstrap from './components/revamp-onboarding-bootstrap/RevampOnboardingBootstrap';

const App = observer(() => {
  const [isSideNavBarOpen, setIsSideNavBarOpen] = useState(false); // for small screens
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false); // for the collapsible sidebar
  const theme = useTheme();
  const isLargeScreen = useMediaQuery(theme.breakpoints.up('md'));
  const [openDialogForDnsVerification, setOpenDialogForDnsVerification] =
    useState(false);
  const { notify } = useAlertStore();
  const {
    getUserDetails,
    getOnBoardingCompleteStatus,
    fetchUserDetails,
    isRevampExperience,
  } = useAuthStore();
  const userDetails = getUserDetails();
  const revampExperience = isRevampExperience();
  // The product mock uses a fixed navigation rail. Keep the legacy collapse
  // preference intact for non-rollout organizations, but never let a collapse
  // state carried across an organization switch distort the revamp shell.
  const effectiveSidebarCollapsed = revampExperience
    ? false
    : isSidebarCollapsed;
  const { organizationDetails, getDnsVerification } = useOrganizationStore();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const languageStore = useLanguageStore();
  const { isDashboardTourCompleted, setDashboardTourCompleted } =
    useUserSettingsStore();
  const [isDashboardTourOpen, setIsDashboardTourOpen] = useState(false);
  const [consentChecked, setConsentChecked] = useState(false);
  const [consentError, setConsentError] = useState<string | null>(null);
  const [consentSaving, setConsentSaving] = useState(false);
  const { t } = useTranslation();

  const dnsVerificationAllowedRoles: Array<keyof typeof ROLES> = [
    ROLES.USER,
    ROLES.ADMIN,
    ROLES.SUPER_ADMIN,
  ];

  const checkInvitationToken = async () => {
    const invitationToken = searchParams.get(SEARCH_PARAMS.invitationToken);

    if (!invitationToken) {
      return;
    }

    // The user will be logged in at this point cause this component is wrapped with ProtectedRoute component

    // There could be a clashing scenario, where the invite link is also there and the user is also new

    const isOnboarded = await getOnBoardingCompleteStatus();

    if (isOnboarded) {
      // The user is not new, he has completed the new user onboarding once for some org
      navigate(
        `${PATHS.onBoardRegistration}?${SEARCH_PARAMS.entry}=${ENTRY_CASES.joinOrg}&invitationToken=${invitationToken}`,
      );

      return;
    }

    // When a brand new user signs up using an invitation link we should
    // directly start the join organization flow and avoid creating a default
    // organization.
    navigate(
      `${PATHS.onBoardRegistration}?${SEARCH_PARAMS.entry}=${ENTRY_CASES.joinOrg}&invitationToken=${invitationToken}`,
    );
  };

  const checkDnsVerifyCalledByUser = async () => {
    const dnsVerify = searchParams.get(SEARCH_PARAMS.dnsVerify);

    if (!dnsVerify) {
      return;
    }

    if (!userDetails) {
      notify(UNHANDLED_ERROR, 'error');

      return;
    }

    if (!dnsVerificationAllowedRoles.includes(userDetails.role)) {
      return;
    }

    const dnsVerificationResponse = await getDnsVerification();

    if (!dnsVerificationResponse.success || !dnsVerificationResponse.content) {
      notify(dnsVerificationResponse.message ?? UNHANDLED_ERROR, 'error');

      return;
    }

    const dnsVerification = dnsVerificationResponse.content;

    if (!dnsVerification.isDnsVerified) {
      setOpenDialogForDnsVerification(true);
    } else {
      setOpenDialogForDnsVerification(false);
    }
  };

  useEffect(() => {
    const fetchSearchParamData = async () => {
      await Promise.all([checkInvitationToken(), checkDnsVerifyCalledByUser()]);
    };

    fetchSearchParamData();
  }, [searchParams]);

  useEffect(() => {
    const fetchInitialData = async () => {
      await checkAchievements();
    };

    fetchInitialData();
    languageStore.fetchLanguage(organizationDetails?.preferredLanguage);
  }, []);

  const isSettingsRoute = location.pathname === PATHS.settings;
  const isAdultConsentRequired = Boolean(
    userDetails && !userDetails.adultConsentConfirmed && !isSettingsRoute,
  );

  useEffect(() => {
    if (isAdultConsentRequired) {
      setIsDashboardTourOpen(false);

      return;
    }

    if (
      !revampExperience &&
      !isDashboardTourCompleted &&
      userDetails?.adultConsentConfirmed
    ) {
      setIsDashboardTourOpen(true);

      return;
    }

    setIsDashboardTourOpen(false);
  }, [
    isAdultConsentRequired,
    isDashboardTourCompleted,
    revampExperience,
    userDetails?.adultConsentConfirmed,
    location.pathname,
    location.search,
  ]);

  useEffect(() => {
    if (!userDetails) {
      return;
    }

    trackDashboardPageView({
      user: userDetails,
      pathname: location.pathname,
      search: location.search,
    });
  }, [location.pathname, location.search, userDetails]);

  useEffect(() => {
    if (!isAdultConsentRequired) {
      setConsentChecked(false);
      setConsentError(null);
    }
  }, [isAdultConsentRequired]);

  const handleAdultConsentSave = async () => {
    if (!userDetails) {
      return;
    }

    if (!consentChecked) {
      setConsentError(t('adult_consent_required'));

      return;
    }

    try {
      setConsentSaving(true);
      const { success, message } = await updateAdultConsent({
        adultConsentConfirmed: true,
      });

      if (!success) {
        notify(message ?? UNHANDLED_ERROR, 'error');

        return;
      }

      await fetchUserDetails();
      notify(t('adult_consent_save_success'), 'success');
    } catch (error) {
      await notifyUnhandledError(error, notify);
    } finally {
      setConsentSaving(false);
    }
  };

  const checkAchievements = async () => {
    try {
      const tzOffset = String(new Date().getTimezoneOffset());
      const { success, content } = await getAchievementStatuses(tzOffset);

      if (!success || !content) {
        return;
      }

      for (const achievement of content as AchievementStatus[]) {
        if (
          achievement.completed &&
          !sessionStorage.getItem(`achievement_${achievement.key}`)
        ) {
          notify(`Achievement unlocked: ${achievement.description}`, 'success');
          sessionStorage.setItem(`achievement_${achievement.key}`, 'true');
          break;
        }
      }
    } catch (error) {
      // silently ignore errors as this is non-blocking
    }
  };

  return (
    <Box
      data-dashboard-visual-system={revampExperience ? 'revamp' : 'legacy'}
      sx={{
        height: '100vh',
        display: 'flex',
        flexDirection: 'row',
        bgcolor: revampExperience ? 'background.default' : undefined,
      }}
    >
      <CssBaseline />
      <RevampOnboardingBootstrap />

      {/* Sidebar container */}
      <Box
        sx={{
          display: { xs: 'none', sm: 'grid' },
          width: revampExperience
            ? isLargeScreen
              ? effectiveSidebarCollapsed
                ? 72
                : 248
              : 0
            : isLargeScreen
              ? {
                  md: effectiveSidebarCollapsed
                    ? styleVariables.sidebarCollapsedWidthMd
                    : styleVariables.sidebarOpenWidthMd,
                  lg: effectiveSidebarCollapsed
                    ? styleVariables.sidebarCollapsedWidthLg
                    : styleVariables.sidebarOpenWidthLg,
                  xl: effectiveSidebarCollapsed
                    ? styleVariables.sidebarCollapsedWidthXl
                    : styleVariables.sidebarOpenWidthXl,
                }
              : 0,
          borderRight: revampExperience
            ? '1px solid'
            : `1px solid ${styleVariables.chineseBlack}`,
          borderColor: revampExperience ? 'divider' : undefined,
        }}
        className={styles['nav-bar-container']}
      >
        <SideNavBar
          open={isSideNavBarOpen}
          onChange={setIsSideNavBarOpen}
          collapsed={effectiveSidebarCollapsed}
          setCollapsed={setIsSidebarCollapsed}
        />
      </Box>

      {/* Main content container */}
      <Box
        sx={{
          bgcolor: revampExperience
            ? 'background.default'
            : styleVariables.sealBrown,
          flex: 1,
          transition: 'margin 0.3s',
        }}
        className={styles['module-content-container']}
      >
        <CustomAppBar setIsSideNavBarOpen={setIsSideNavBarOpen} />
        <Paper
          className="hanabi-scrollbar"
          variant="outlined"
          component="main"
          sx={{
            overflowX: 'hidden',
            overflowY: 'auto',
            height: '100%',
            margin: revampExperience ? 0 : '8px',
            padding: revampExperience ? 0 : '8px',
            border: revampExperience
              ? 0
              : `1px solid ${styleVariables.chineseBlack}`,
            borderRadius: revampExperience ? 0 : undefined,
            bgcolor: revampExperience
              ? 'background.default'
              : 'background.paper',
          }}
        >
          <BillingTrialBanner />
          <Outlet />
        </Paper>
        {!revampExperience && <Footer />}
      </Box>
      {openDialogForDnsVerification && (
        <DnsVerification
          isOpen={openDialogForDnsVerification}
          setIsOpen={setOpenDialogForDnsVerification}
        />
      )}
      <DashboardTour
        isOpen={isDashboardTourOpen}
        onComplete={() => {
          setDashboardTourCompleted(true);
          setIsDashboardTourOpen(false);
        }}
        onSkip={() => {
          setDashboardTourCompleted(true);
          setIsDashboardTourOpen(false);
        }}
      />
      <UpgradeRequestPopup />
      <AdultConsentDialog
        open={isAdultConsentRequired}
        checked={consentChecked}
        loading={consentSaving}
        errorText={consentError ?? undefined}
        onOpenSecuritySettings={() =>
          navigate(`${PATHS.settings}?tab=security`)
        }
        onCheckedChange={(checked) => {
          setConsentError(null);
          setConsentChecked(checked);
        }}
        onSave={handleAdultConsentSave}
      />
    </Box>
  );
});

export default App;
